import { MoreVertRounded } from "@mui/icons-material";
import {
  Box,
  Card,
  Chip,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  styled,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { downloadEncodedUri, useDestructiveConfirm } from "src/utilities";
import { Chart, EditMetricModal, LinkBehavior } from "..";
import { Links } from "../../constants";
import { MetricDto, useDeleteMetric } from "../../http";
import { MetricMapper } from "../../mapping";

const MetricLink = styled(Link)`
  &:hover {
    text-decoration: none;
  }
`;

const MetricCard = styled(Card)`
  height: 100%;
  padding: ${({ theme }) => theme.spacing(1)};
  transition: ${({ theme }) => theme.transitions.create("background-color")};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.transparent5};
    text-decoration: none;
  }
`;

interface IProps {
  metric: MetricDto;
  metricIndex: number;
  userCanRemove?: (metric: MetricDto) => boolean;
  sx?: SxProps;
  onMetricRemoved?: (metricGuid: string) => void;
}

const MetricsGridItem = (props: IProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { mutate: deleteMetric } = useDeleteMetric();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const destructiveConfirm = useDestructiveConfirm();
  const theme = useTheme();

  function handlExportCsvClicked() {
    setMenuAnchor(null);

    const csvData = MetricMapper.toCsvData(props.metric);

    downloadEncodedUri({
      filename: `${DateTime.now().toISODate()} - ${props.metric.title}`,
      encodedUri: encodeURI(`data:text/csv;chartset=utf-8,${csvData}`),
    });
  }

  async function handleDeleteClicked() {
    setMenuAnchor(null);

    const { confirmed } = await destructiveConfirm({
      title: `Delete Metric`,
      content: `Are you sure you want to delete this Metric?`,
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteMetric(
      {
        assignedTeamSlug: props.metric.assignedTeam.slug,
        guid: props.metric.guid,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(`Metric has been deleted`);
        },
      },
    );
  }

  async function handleRemoveClicked() {
    setMenuAnchor(null);

    const { confirmed } = await confirm({
      title: `Remove Metric`,
      content: `Are you sure you want to remove this Metric?`,
      confirmationText: "Remove",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    props.onMetricRemoved?.(props.metric.guid);
  }

  const hasData = props.metric.datasets.length > 0 && props.metric.datasets.some((dataset) => dataset.data.length > 0);

  return (
    <>
      <MetricLink href={Links.MetricDetail(props.metric.guid)}>
        <MetricCard sx={{ p: { xs: 0.5, sm: 1 }, ...props.sx }}>
          <Stack spacing={0.5} sx={{ height: "100%" }}>
            <Stack spacing={0.5} direction="row">
              {props.metric.assignedTeam && (
                <Chip
                  label={props.metric.assignedTeam.name}
                  color="primary"
                  component={LinkBehavior}
                  href={Links.Team(props.metric.assignedTeam.slug)}
                  clickable
                />
              )}
              {props.metric.reportingUser && (
                <Chip
                  label={props.metric.reportingUser.displayName}
                  color="person"
                  component={LinkBehavior}
                  href={Links.User(props.metric.reportingUser.userId)}
                  clickable
                />
              )}
            </Stack>
            <Stack
              spacing={0.5}
              direction="row"
              sx={{ display: "flex", alignItems: "start", justifyContent: "space-between" }}
            >
              <Typography variant="h5">{props.metric.title}</Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  setMenuAnchor(e.currentTarget);
                }}
              >
                <MoreVertRounded />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem onClick={handlExportCsvClicked}>Export CSV</MenuItem>

                {props.metric.userCanEdit && (
                  <>
                    <MenuItem
                      onClick={() => {
                        setIsModalVisible(true);
                        setMenuAnchor(null);
                      }}
                    >
                      Update
                    </MenuItem>
                  </>
                )}

                {props.userCanRemove?.(props.metric) && <MenuItem onClick={handleRemoveClicked}>Remove</MenuItem>}

                {props.metric.userCanEdit && (
                  <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
                    Delete
                  </MenuItem>
                )}
              </Menu>
            </Stack>
            {props.metric.description && <Typography>{props.metric.description}</Typography>}
            {props.metric.tags.length > 0 && (
              <Stack spacing={0.5} direction="row" flexWrap="wrap">
                {props.metric.tags.map((tag) => (
                  <Chip key={tag} label={tag} />
                ))}
              </Stack>
            )}
            {hasData ? (
              <>
                <Chart
                  datasets={MetricMapper.toChartDataWithNulls(props.metric).map((data) => {
                    return {
                      title: data.title,
                      displayAsBar: data.displayAsBar,
                      color: data.color,
                      dotted: data.dotted,
                      data: data.chartData,
                      sortOrder: data.sortOrder,
                    };
                  })}
                  annotations={props.metric.annotations}
                  sx={{ flex: 1, px: 0.5 }}
                />
                {props.metric.dataLastModifiedDateTimeUtc && props.metric.dataLastModifiedByDisplayName && (
                  <Box>
                    <Typography variant="caption">{`Data updated by ${
                      props.metric.dataLastModifiedByDisplayName
                    } on ${DateTime.fromISO(props.metric.dataLastModifiedDateTimeUtc, { zone: "utc" })
                      .toLocal()
                      .toLocaleString(DateTime.DATE_FULL)}`}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box display="flex" flex={1} alignItems="center" justifyContent="center">
                <Typography variant="placeholder">No data to display</Typography>
              </Box>
            )}
          </Stack>
        </MetricCard>
      </MetricLink>

      <EditMetricModal
        isVisible={isModalVisible}
        metric={props.metric}
        onUpdated={() => setIsModalVisible(false)}
        onCancelled={() => setIsModalVisible(false)}
      />
    </>
  );
};

export { MetricsGridItem };
